import { useRef, useState } from "react";
import { Camera, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { uploadMedia, deleteMedia } from "@/lib/profile/storage";
import { MAX_PHOTOS } from "@/lib/profile/types";
import { useAuth } from "@/lib/auth/AuthProvider";
import { toast } from "sonner";
import SignedImage from "@/components/media/SignedImage";

interface PhotoGridProps {
  value: string[];
  onChange: (urls: string[]) => void;
  max?: number;
}

const PhotoGrid = ({ value, onChange, max = MAX_PHOTOS }: PhotoGridProps) => {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const slotCount = max;
  const remaining = max - value.length;

  const handleFiles = async (files: FileList | null) => {
    if (!files || !user) return;
    const accepted = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (!accepted.length) return;
    if (accepted.length > remaining) {
      toast.error(`You can add ${remaining} more photo${remaining === 1 ? "" : "s"}.`);
      return;
    }
    setUploading(true);
    try {
      const uploaded: string[] = [];
      for (const file of accepted) {
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name} is over 10 MB.`);
          continue;
        }
        const url = await uploadMedia("avatars", user.id, file, "photo", "jpg");
        uploaded.push(url);
      }
      onChange([...value, ...uploaded]);
    } catch (e) {
      toast.error((e as Error).message || "Upload failed.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeAt = async (i: number) => {
    const url = value[i];
    onChange(value.filter((_, idx) => idx !== i));
    void deleteMedia("avatars", url);
  };

  const onDragStart = (i: number) => setDragIdx(i);
  const onDragOver = (e: React.DragEvent) => e.preventDefault();
  const onDrop = (i: number) => {
    if (dragIdx === null || dragIdx === i) return;
    const next = [...value];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(i, 0, moved);
    onChange(next);
    setDragIdx(null);
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
        {Array.from({ length: slotCount }).map((_, i) => {
          const url = value[i];
          if (url) {
            const isPrimary = i === 0;
            return (
              <div
                key={url + i}
                draggable
                onDragStart={() => onDragStart(i)}
                onDragOver={onDragOver}
                onDrop={() => onDrop(i)}
                className="relative aspect-square overflow-hidden rounded-xl border border-border bg-secondary cursor-move group"
              >
                <SignedImage src={url} alt="" className="h-full w-full object-cover" />
                {isPrimary && (
                  <span className="absolute top-1.5 left-1.5 rounded-full bg-foreground/90 px-2 py-0.5 text-[10px] font-medium text-background">
                    Main
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => removeAt(i)}
                  className="absolute top-1.5 right-1.5 rounded-full bg-background/80 p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-background"
                  aria-label="Remove photo"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          }
          if (i === value.length) {
            return (
              <button
                key={`add-${i}`}
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                className="aspect-square rounded-xl border-2 border-dashed border-border flex flex-col items-center justify-center gap-1.5 text-muted-foreground hover:border-foreground hover:text-foreground transition-colors disabled:opacity-50"
              >
                {uploading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Camera className="h-5 w-5" />
                )}
                <span className="text-[10px] font-medium uppercase tracking-wider">
                  {uploading ? "Uploading" : "Add"}
                </span>
              </button>
            );
          }
          return (
            <div
              key={`empty-${i}`}
              className="aspect-square rounded-xl border border-dashed border-border/50"
            />
          );
        })}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
      <p className="text-xs text-muted-foreground">
        {value.length} of {max} · drag to reorder · first photo is your main.
      </p>
    </div>
  );
};

export default PhotoGrid;
